import axiosInstance, { axiosInstanceImage } from "@helper/interceptor";

export const getPublicTree = async (tid) => {
  const response = await axiosInstance.get(`/public/trees/${tid}`);
  return response.data;
};
export const getTree = async (tid) => {
  // Request tree data - backend should use InstallationNameSerializer for installations
  const response = await axiosInstance.get(`trees/${tid}`);
  console.log("getTree API response for tree", tid, ":", response.data);
  if (response.data.installations) {
    console.log(
      "Tree installations with sensors:",
      response.data.installations.map((inst) => ({
        id: inst.id,
        sensor: inst.sensor,
      }))
    );
  }
  return response.data;
};
export const getTrees = async (params = {}) => {
  const {
    page = 1,
    pageSize = 100,
    search = "",
    project_id,
    without_project,
    ordering,
    area,
    status,
  } = params;
  let response = await axiosInstance.get("trees/", {
    params: {
      page,
      page_size: pageSize,
      search,
      ...(project_id && { project_id }), // Only add if provided
      ...(without_project && { without_project }), // Only add if provided
      ...(ordering && { ordering }), // Only add if provided
      ...(area && { area }),
      ...(status && { status }),
    },
    timeout: 30000, // 30 second timeout for large datasets
  });
  return response.data;
};

// Fetch all trees using the lightweight list serializer.
// With ~200 bytes/tree the entire dataset fits in one request for most customers.
// Falls back to parallel pagination if the backend has a hard MAX_PAGE_SIZE cap.
export const getAllTrees = async () => {
  const pageSize = 1000;

  let first;
  try {
    first = await getTrees({ page: 1, pageSize });
  } catch (error) {
    console.warn("Failed to load trees:", error);
    return [];
  }

  if (!first.results) return Array.isArray(first) ? first : [];

  const allTrees = [...first.results];
  if (!first.next) return allTrees; // common case: everything in one page

  // Fallback: backend capped page_size below what we requested
  const effectivePageSize = first.results.length;
  const totalPages = Math.ceil(first.count / effectivePageSize);
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

  const CONCURRENCY = 3;
  for (let i = 0; i < remainingPages.length; i += CONCURRENCY) {
    const batch = remainingPages.slice(i, i + CONCURRENCY);
    const responses = await Promise.allSettled(
      batch.map((page) => getTrees({ page, pageSize: effectivePageSize }))
    );
    responses.forEach((result, idx) => {
      if (result.status === "fulfilled" && result.value.results) {
        allTrees.push(...result.value.results);
      } else if (result.status === "rejected") {
        console.warn(`Failed to load trees page ${batch[idx]}:`, result.reason);
      }
    });
  }

  return allTrees;
};

// Fetch all trees without project, including full installation data for DataScience
export const getTreesWithoutProject = async () => {
  try {
    const response = await axiosInstance.get("trees-without-project/", {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.warn("Failed to load trees without project:", error);
    return [];
  }
};
export const addTreeImage = async ({ form, tid }) => {
  // Use dedicated image instance with proper multipart handling
  let response = await axiosInstanceImage.post(`trees/${tid}/upload-image`, form);
  return response.data;
};

export const uploadTree3DModel = async ({ tid, form }) => {
  const response = await axiosInstanceImage.post(`trees/${tid}/upload-3d-model/`, form);
  return response.data;
};

export const removeTree3DModel = async (tid) => {
  const response = await axiosInstance.delete(`trees/${tid}/remove-3d-model/`);
  return response.data;
};

export const deleteTreeImage = async (imageId) => {
  let response = await axiosInstance.delete(`tree-images/${imageId}/`);
  return response.data;
};
export const fetchTreeSpecies = async (params = {}) => {
  try {
    const res = await axiosInstance.get(`tree-species/`, {
      params: { fields: "id,name,de_name,calibration_species", ...params },
    });
    return res.data;
  } catch (error) {
    if (error?.response?.status === 400) {
      const res = await axiosInstance.get(`tree-species/`, { params });
      return res.data;
    }
    throw error;
  }
};
export const createTree = async (formData) => {
  let response = await axiosInstance.post("trees/", formData);
  return response.data;
};
export const addExistingTrees = async (formData) => {
  try {
    let response = await axiosInstance.patch(`add-trees-project/${formData.id}/`, formData);
    return response.data;
  } catch (error) {
    return error.response.data;
  }
};
export const updateTree = async ({ formData, id }) => {
  let response = await axiosInstance.patch(`trees/${id}/`, formData);
  return response.data;
};
export const updateTreeThreshold = async ({ formData, id }) => {
  let response = await axiosInstance.patch(`trees-thresholds/${id}/`, formData);
  return response.data;
};
export const updateTreeSoil = async ({ formData, id }) => {
  let response = await axiosInstance.patch(`trees-soil/${id}/`, formData);
  return response.data;
};
export const deleteTree = async (id) => {
  let response = await axiosInstance.delete(`trees/${id}`);
  return response.data;
};

export const uploadTree = async ({ formdata }) => {
  // Use dedicated image instance for file uploads
  const res = await axiosInstanceImage.post(`upload-csv`, formdata);
  return res.data;
};
// Keep old method for backward compatibility if needed
export const addTreeMaintenance = async ({ formdata, id }) => {
  const res = await axiosInstance.post(`trees/${id}/add-maintenance`, formdata);
  return res.data;
};

// Get trees without sensors (possible child trees)
export const getPossibleChildTrees = async () => {
  const response = await axiosInstance.get("possible-child-trees/");
  return response.data;
};

// Get trees with sensors (possible parent trees)
export const getPossibleParentTrees = async () => {
  const response = await axiosInstance.get("possible-parent-trees/");
  return response.data;
};

export const addInstallation = async ({ formdata }) => {
  // The new InstallationNameSerializer should include sensor and tree names
  const res = await axiosInstance.post(`installations/`, formdata);
  console.log("addInstallation API response:", res.data);
  return res.data;
};
export const deleteInstallation = async (id) => {
  const res = await axiosInstance.delete(`installations/${id}`);
  return res.data;
};

export const updateInstallation = async ({ formdata, id }) => {
  // The new InstallationNameSerializer should include sensor and tree names
  const res = await axiosInstance.patch(`installations/${id}/`, formdata);
  console.log("updateInstallation API response:", res.data);
  return res.data;
};

//fetch download data for download
const hasNestedTimeData = (node) => {
  if (!node || typeof node !== "object") return false;
  return Object.values(node).some((value) => {
    if (!value || typeof value !== "object") return false;
    if (Array.isArray(value.time) && value.time.length > 0) return true;
    return hasNestedTimeData(value);
  });
};

const hasAnyNonTimeValues = (dataNode) => {
  if (!dataNode || typeof dataNode !== "object") return false;
  return Object.values(dataNode).some((sensorNode) => {
    if (!sensorNode || typeof sensorNode !== "object") return false;
    return Object.entries(sensorNode).some(([field, values]) => {
      if (field === "time" || field === "excel_time") return false;
      if (!Array.isArray(values)) return false;
      return values.some((v) => v !== "" && v !== null && v !== undefined && `${v}`.trim() !== "");
    });
  });
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const createSensorExportJob = async ({ exportType, selectedItems, startDate, endDate }) => {
  const res = await axiosInstance.post("export/jobs/", {
    export_type: exportType,
    selected_items: selectedItems,
    start: startDate || null,
    end: endDate || null,
    aggregation_type: "download",
  });
  return res.data;
};

const getSensorExportJob = async (jobId) => {
  const res = await axiosInstance.get(`export/jobs/${jobId}/`);
  return res.data;
};

const getSensorExportJobResult = async (jobId) => {
  const res = await axiosInstance.get(`export/jobs/${jobId}/result/`);
  return res.data;
};

const waitForSensorExportJob = async (jobId) => {
  const maxWaitMs = 15 * 60 * 1000;
  const pollMs = 2000;
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const status = await getSensorExportJob(jobId);
    if (status?.status === "done") {
      return getSensorExportJobResult(jobId);
    }
    if (status?.status === "failed") {
      throw new Error(status?.error || "Download failed.");
    }
    await sleep(pollMs);
  }
  throw new Error("Download is taking too long. Please try a shorter date range.");
};

export const fetchDownloadTreeData = async ({
  tree,
  sensors,
  parameterQuery,
  startDate,
  endDate,
}) => {
  const tsps = sensors.map((sensor) => `${tree.id}+${sensor.id}`);
  const tree_sensors_joined = tsps.join(",");
  const selectedItems = `${tree_sensors_joined}-${parameterQuery}`;
  try {
    const job = await createSensorExportJob({
      exportType: "tree",
      selectedItems,
      startDate,
      endDate,
    });
    const asyncResult = await waitForSensorExportJob(job.job_id);
    if (hasNestedTimeData(asyncResult?.data) && hasAnyNonTimeValues(asyncResult?.data)) {
      return asyncResult;
    }
  } catch (error) {
    console.warn("Async tree export failed, falling back to legacy download endpoint.", error);
  }
  const endpoint =
    startDate && endDate
      ? `data/${tree_sensors_joined}-${parameterQuery}/${startDate}:${endDate}/download`
      : `data/${tree_sensors_joined}-${parameterQuery}/download`;
  let res = await axiosInstance.get(endpoint, { timeout: 60000 });
  if (startDate && endDate && !hasNestedTimeData(res?.data?.data)) {
    res = await axiosInstance.get(`data/${tree_sensors_joined}-${parameterQuery}/download`, {
      timeout: 60000,
    });
  }
  return res.data;
};
export const fetchDownloadSensorData = async ({ sensors, parameterQuery, startDate, endDate }) => {
  const selectedItems = `${sensors}-${parameterQuery}`;
  try {
    const job = await createSensorExportJob({
      exportType: "sensor",
      selectedItems,
      startDate,
      endDate,
    });
    const asyncResult = await waitForSensorExportJob(job.job_id);
    if (hasNestedTimeData(asyncResult?.data) && hasAnyNonTimeValues(asyncResult?.data)) {
      return asyncResult;
    }
  } catch (error) {
    console.warn("Async sensor export failed, falling back to legacy download endpoint.", error);
  }
  const endpoint =
    startDate && endDate
      ? `ts_data/${sensors}-${parameterQuery}/${startDate}:${endDate}/download`
      : `ts_data/${sensors}-${parameterQuery}/download`;
  let res = await axiosInstance.get(endpoint, { timeout: 60000 });
  if (startDate && endDate && !hasNestedTimeData(res?.data?.data)) {
    res = await axiosInstance.get(`ts_data/${sensors}-${parameterQuery}/download`, {
      timeout: 60000,
    });
  }
  return res.data;
};
//fetch download data for download
export const fetchTSDataByDate = async (props) => {
  const res = await axiosInstance.get("influx-data/", {
    params: {
      start: props.startDate,
      stop: props.endDate,
      interval: props.interval,
      installations: props.installations,
      field: props.field,
    },
    timeout: 60000,
  });
  return res.data;
};

export const fetchTSDataMeanByDate = async (props) => {
  const res = await axiosInstance.get("influx-data/", {
    params: {
      start: props.startDate,
      stop: props.endDate,
      interval: "1d",
      treeSensors: props.tsps,
      field: props.field,
    },
    timeout: 60000,
  });
  return res.data;
};

// fetch sensor-data by date
export const fetchRainDataByDate = async (props) => {
  const res = await axiosInstance.get(
    `weather/${props.id}/rain/${props.startDate}:${props.endDate}`
  );
  return res.data;
};

export const fetchTreeHealthByDate = async (props) => {
  const res = await axiosInstance.get(
    `health_sensor/${props.id}/${props.startDate}:${props.endDate}`
  );
  return res.data;
};

export const getTreeImages = async (treeId) => {
  let response = await axiosInstance.get(`tree-images/${treeId}`);
  return response.data;
};

export const refreshTreeHealth = async (treeId) => {
  let response = await axiosInstance.post(`trees/${treeId}/refresh_health/`);
  return response.data;
};
